import { getFWN } from '@core/fwn/getFWN'

const PATCH_MARKER = '__fwPatched'

type DataLayerValue = Record<string, unknown> | string | number | undefined

type DataLayerArray = DataLayerValue[] & {
  [PATCH_MARKER]?: boolean
}

const TRACKED_EVENTS = new Set(['add_to_cart', 'purchase'])

interface EcommerceData {
  items?: unknown
  value?: unknown
  currency?: unknown
  transaction_id?: unknown
}

function extractGtmEvent(
  entry: Record<string, unknown>,
): { eventName: string; data: EcommerceData } | null {
  const eventName = entry?.event
  if (typeof eventName !== 'string' || !TRACKED_EVENTS.has(eventName)) {
    return null
  }

  const ecommerce = entry.ecommerce as Record<string, unknown> | undefined
  return {
    eventName,
    data: {
      items: ecommerce?.items ?? entry.items,
      value: ecommerce?.value ?? entry.value,
      currency: ecommerce?.currency ?? entry.currency,
      transaction_id: ecommerce?.transaction_id ?? entry.transaction_id,
    },
  }
}

function extractGtagEvent(
  args: DataLayerValue[],
): { eventName: string; data: EcommerceData } | null {
  if (args[0] !== 'event') {
    return null
  }

  const eventName = args[1]
  if (typeof eventName !== 'string' || !TRACKED_EVENTS.has(eventName)) {
    return null
  }

  const params = args[2] as Record<string, unknown> | undefined
  return {
    eventName,
    data: {
      items: params?.items,
      value: params?.value,
      currency: params?.currency,
      transaction_id: params?.transaction_id,
    },
  }
}

function buildUserData(data: EcommerceData): Record<string, unknown> {
  return {
    source: 'ga_datalayer',
    ...data,
  }
}

const EVENT_DISPATCHERS: Record<string, (data: EcommerceData) => void> = {
  add_to_cart: (data) => {
    getFWN()?.analytics?.addToCart(buildUserData(data))
  },
  purchase: (data) => {
    getFWN()?.analytics?.purchase(buildUserData(data))
  },
}

function dispatchEvent(eventName: string, data: EcommerceData) {
  EVENT_DISPATCHERS[eventName]?.(data)
}

/**
 * gtag() pushes an Arguments object as a single item: dataLayer.push(arguments).
 * Detect this and convert to a flat array so extractGtagEvent can match it.
 */
function normalizeArgs(args: DataLayerValue[]): DataLayerValue[] {
  if (
    args.length === 1 &&
    typeof args[0] === 'object' &&
    args[0] !== null &&
    typeof (args[0] as Record<string, unknown>).length === 'number' &&
    (args[0] as Record<string, unknown>)[0] !== undefined
  ) {
    return Array.from(args[0] as unknown as ArrayLike<DataLayerValue>)
  }
  return args
}

function interceptArgs(args: DataLayerValue[]) {
  const normalized = normalizeArgs(args)

  for (const entry of normalized) {
    if (typeof entry === 'object' && entry !== null) {
      const match = extractGtmEvent(entry)
      if (match) {
        dispatchEvent(match.eventName, match.data)
        return
      }
    }
  }

  const match = extractGtagEvent(normalized)
  if (match) {
    dispatchEvent(match.eventName, match.data)
  }
}

/**
 * Patches window.dataLayer.push with a single stable interceptor exposed via
 * getter/setter. When a later script (e.g. GTM) reassigns .push, the setter
 * stores it as middleware — the interceptor itself never changes. Re-entrant
 * calls (GTM calling back the captured interceptor) short-circuit to nativePush,
 * so each event is dispatched exactly once regardless of how many layers exist.
 */
export function patchDataLayer() {
  const w = window as Window & { dataLayer?: DataLayerArray }
  w.dataLayer = w.dataLayer || ([] as unknown as DataLayerArray)

  if (w.dataLayer[PATCH_MARKER]) {
    return
  }

  // Snapshot entries pushed before this patch (e.g. an inline `purchase` on an
  // order-confirmation page that runs before async fwn.js loads) so we can
  // replay them and stay independent of script load order.
  const preexistingEntries = Array.prototype.slice.call(w.dataLayer)

  const nativePush = Array.prototype.push.bind(w.dataLayer)
  let middleware: ((...args: DataLayerValue[]) => number) | null = null

  // If GA4/GTM already replaced push before our patch, preserve their processor
  const existing = Object.getOwnPropertyDescriptor(w.dataLayer, 'push')
  if (existing?.value && existing.value !== Array.prototype.push) {
    middleware = existing.value
  }

  let isIntercepting = false

  const interceptor = function (...args: DataLayerValue[]): number {
    if (isIntercepting) {
      return nativePush(...args)
    }

    isIntercepting = true
    try {
      const result = middleware
        ? middleware.apply(w.dataLayer, args)
        : nativePush(...args)

      try {
        interceptArgs(args)
      } catch {
        // Never break the customer's GA
      }

      return result
    } finally {
      isIntercepting = false
    }
  }
  ;(interceptor as unknown as Record<string, unknown>)[PATCH_MARKER] = true

  Object.defineProperty(w.dataLayer, 'push', {
    get() {
      return interceptor
    },
    set(newPush: (...args: DataLayerValue[]) => number) {
      if (
        newPush &&
        !(newPush as unknown as Record<string, unknown>)[PATCH_MARKER]
      ) {
        middleware = newPush
      }
    },
    configurable: true,
    enumerable: false,
  })

  Object.defineProperty(w.dataLayer, PATCH_MARKER, {
    value: true,
    enumerable: false,
    configurable: false,
  })

  // Replay pre-existing entries through the same interception path so events
  // pushed before this patch are still captured exactly once.
  for (const entry of preexistingEntries) {
    try {
      interceptArgs([entry])
    } catch {
      // Never break the customer's GA
    }
  }
}
